import { type GetTicketsResponse } from "@mg/schemas/src/christo/catalyst";
import {
  Clock,
  Robot,
  Users,
  File,
  FilePdf,
  Folders,
  FolderPlus,
  StackPlus,
  FolderOpen,
  Play,
} from "@phosphor-icons/react";
import {
  AspectRatio,
  Badge,
  Card,
  Flex,
  Grid,
  Heading,
  HoverCard,
  IconButton,
  Text,
  Tooltip,
} from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { Link, useLoaderData, useRouter } from "@tanstack/react-router";
import cx from "classnames";
import { formatDistanceToNow } from "date-fns";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { validate } from "uuid";

import { TicketActionsDropdown } from "./TicketActionsDropdown";

import { AvatarWithInitials } from "../../../components/AvatarWithInitials";
import { UploadProgress } from "../../../components/UploadProgress";
import { ReactComponent as BrandRobot } from "../../../icons/BrandRobot.svg";
import { ReactComponent as CopyRobot } from "../../../icons/CopyRobot.svg";
import { ReactComponent as LegalRobot } from "../../../icons/LegalRobot.svg";
import { ReactComponent as Spinner } from "../../../images/puntt_loader.svg";
import { ReactComponent as BreatheLoader } from "../../../images/puntt_processing_loader.svg";
import { getFoldersAndTickets, getProject } from "../../../services/projects";
import { useAnalytics } from "../../../utils/analytics";
import { isPunttGuest } from "../../../utils/auth";
import {
  SUPPORTED_EXTENSIONS,
  VIDEO_EXTENSIONS,
} from "../../../utils/constants";
import { useAppSelector } from "../../../utils/hooks";
import { useRevisionMutation } from "../../../utils/queries/projects";
import { authLayoutRoute } from "../../auth-layout/route";
import { ticketsRoute } from "../route";
import { ticketRoute } from "../routes/ticket/route";
import { DragDropProps } from "../view";

type TicketProps = {
  onDeleteTicket(ticketId: string): void;
  onTicketCardClick(ticketId: string): void;
  onFolderCardClick(folderId: string): void;
  revisionMutation: ReturnType<typeof useRevisionMutation>;
  onDeleteFolder(ticketId: string): void;
  onRenameFolder(folderId: string): void;
  onShareTicket(ticketId: string): void;
  onShareFolder(folderId: string): void;
  onDownloadFolder(folderId: string): void;
  onDownloadTicket(ticketId: string): void;
  onDropCards(selectedIds: string[], targetId: string): Promise<void>;
  onManageTicketVersions(ticketId: string): void;
};

type TicketGridProps = TicketProps &
  DragDropProps & {
    uploadState?: any;
    onCancelUpload?: () => void;
  };

interface SelectionRect {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

interface CardRect {
  id: string;
  rect: {
    left: number;
    top: number;
    right: number;
    bottom: number;
  };
}

export function TicketGrid(props: TicketGridProps) {
  const tickets = useAppSelector((state) => state.punttProjects.tickets);
  const folders = useAppSelector((state) => state.punttProjects.folders);

  const setHoveredId = useState<string | null>(null)[1];
  const markedFolders = useMemo(
    () =>
      folders.map((f) => ({
        ...f,
        isFolder: true,
      })),
    [folders],
  );
  const mergedFoldersAndTickets = useMemo(
    () =>
      [...markedFolders, ...tickets].toSorted((a, b) => {
        const dateA = new Date(a.updatedAt as string);
        const dateB = new Date(b.updatedAt as string);

        // Sort in descending order (most recent first)
        return dateB.getTime() - dateA.getTime();
      }),
    [markedFolders, tickets],
  );

  const posthog = useAnalytics();
  const gridRef = useRef<HTMLDivElement>(null);
  const selectionBoxRef = useRef<HTMLDivElement>(null);
  const [cardRects, setCardRects] = useState<CardRect[]>([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [selectionRect, setSelectionRect] = useState<SelectionRect>();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // If no element is focused or focus is in the grid, clear the selection when Escape is pressed
      if (
        document.activeElement === document.body ||
        document.activeElement?.closest("#tickets-grid")
      ) {
        if (e.key === "Escape") {
          posthog.capture("cleared_ticket_selection", {
            num_selected: props.selectedIds.length,
            was_dragging: props.isDraggingCards,
            method: "escape",
          });
          props.setSelectedIds([]);
          props.setIsDraggingCards(false);
          props.setDropTargetId(undefined);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Update card positions when grid size changes or tickets change
  useEffect(() => {
    if (!gridRef.current) return;

    const updateCardRects = () => {
      if (!gridRef.current) return;
      const gridRect = gridRef.current?.getBoundingClientRect();
      if (!gridRect) return;

      const newRects = Array.from(
        gridRef.current.querySelectorAll<HTMLElement>("[data-card-id]"),
      ).map((card) => {
        const rect = card.getBoundingClientRect();
        return {
          id: card.getAttribute("data-card-id") || "",
          rect: {
            left: rect.left - gridRect.left,
            top: rect.top - gridRect.top,
            right: rect.right - gridRect.left,
            bottom: rect.bottom - gridRect.top,
          },
        };
      });
      setCardRects(newRects);
    };

    // Initial calculation
    updateCardRects();

    // Set up ResizeObserver
    const resizeObserver = new ResizeObserver(updateCardRects);
    resizeObserver.observe(gridRef.current);

    return () => resizeObserver.disconnect();
  }, [mergedFoldersAndTickets]); // Recalculate when tickets change

  // Update mouse position handler
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!props.isDraggingCards && selectionRect) {
        setMousePosition({ x: e.clientX, y: e.clientY });
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, [props.isDraggingCards, selectionRect]);

  const selectCardsInDragArea = useCallback(
    (newSelectionRect: SelectionRect, addToSelection = false) => {
      // Calculate selection bounds
      const left = Math.min(newSelectionRect.startX, newSelectionRect.currentX);
      const right = Math.max(
        newSelectionRect.startX,
        newSelectionRect.currentX,
      );
      const top = Math.min(newSelectionRect.startY, newSelectionRect.currentY);
      const bottom = Math.max(
        newSelectionRect.startY,
        newSelectionRect.currentY,
      );

      // Find intersecting cards
      const intersectingIds = cardRects
        .filter(({ rect }) => {
          return !(
            rect.right < left ||
            rect.left > right ||
            rect.bottom < top ||
            rect.top > bottom
          );
        })
        .map(({ id }) => id)
        .filter(
          (id) => !props.isInProgress.has(id) && !props.isRemoving.has(id),
        );

      // Update selection - if Ctrl/Cmd is held, add to existing selection
      if (addToSelection) {
        props.setSelectedIds((prev) =>
          Array.from(new Set([...prev, ...intersectingIds])),
        );
      } else {
        props.setSelectedIds(intersectingIds);
      }
      if (intersectingIds.length) {
        props.setLastSelectedId(intersectingIds[0]);
      }
    },
    [cardRects, props.isInProgress, props.isRemoving],
  );

  // Modify scroll handler to use tracked mouse position
  useEffect(() => {
    if (!selectionRect) return;

    const handleScroll = () => {
      if (!gridRef.current) return;
      const gridRect = gridRef.current?.getBoundingClientRect();
      if (!gridRect) return;

      const newSelectionRect = {
        ...(selectionRect as SelectionRect),
        currentX: mousePosition.x - gridRect.left,
        currentY: mousePosition.y - gridRect.top,
      };
      setSelectionRect(newSelectionRect);

      selectCardsInDragArea(newSelectionRect, props.isModifierKeyPressed);
    };

    document.addEventListener("scroll", handleScroll, true);
    return () => document.removeEventListener("scroll", handleScroll, true);
  }, [selectionRect, mousePosition, props.isModifierKeyPressed]);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't start selection if we're still in dragging state
    if (props.isDraggingCards) return;

    // Ignore if clicking on a card
    if ((e.target as HTMLElement).closest("[data-card-id]")) return;

    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Clear selection when clicking empty space, unless Ctrl/Cmd is held
    if (!e.metaKey && !e.ctrlKey) {
      props.setSelectedIds([]);

      posthog.capture("cleared_ticket_selection", {
        num_selected: props.selectedIds.length,
        was_dragging: props.isDraggingCards,
        method: "click",
      });
    }

    setSelectionRect({
      startX: e.clientX - rect.left,
      startY: e.clientY - rect.top,
      currentX: e.clientX - rect.left,
      currentY: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // Don't update selection if we're dragging
    if (props.isDraggingCards) return;

    if (selectionRect) {
      const rect = gridRef.current?.getBoundingClientRect();
      if (!rect) return;

      const newSelectionRect = {
        ...(selectionRect ?? {
          startX: e.clientX - rect.left,
          startY: e.clientY - rect.top,
        }),
        currentX: e.clientX - rect.left,
        currentY: e.clientY - rect.top,
      };
      setSelectionRect(newSelectionRect);

      selectCardsInDragArea(newSelectionRect, e.ctrlKey || e.metaKey);
    }
  };

  const handleMouseUp = () => {
    if (selectionRect) {
      posthog.capture("finish_drag_selection", {
        total_selected: props.selectedIds.length,
        drag_area: Math.abs(
          (selectionRect.currentX - selectionRect.startX) *
            (selectionRect.currentY - selectionRect.startY),
        ),
      });

      setSelectionRect(undefined);
    }
    // Reset drag state on mouse up
    props.setIsDraggingCards(false);
    props.setDropTargetId(undefined);
  };

  return (
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div
      ref={gridRef}
      className="relative px-10"
      role="grid"
      id="tickets-grid"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <Flex gap="5" wrap="wrap">
        {mergedFoldersAndTickets.map((t, i) => {
          const title = "title" in t ? t.title : t.name;
          if (validate(t._id)) {
            if (t.isFolder && "name" in t) {
              return <SkeletonFolder key={t.name} directoryName={t.name} />;
            }

            // Use TicketCard for uploading
            return (
              <div
                key={`.${i}.${title}`}
                data-card-id={t._id}
                className={cx(
                  "relative z-[1] border-2 transition-all duration-200 ease-out",
                  {
                    "border-puntt-blue-9": props.selectedIds.includes(t._id),
                    "border-transparent": !props.selectedIds.includes(t._id),
                    "opacity-50":
                      props.isDraggingCards &&
                      props.selectedIds.includes(t._id),
                    "scale-[1.02]": props.selectedIds.includes(t._id),
                    "hover:scale-[1.04]": props.selectedIds.includes(t._id),
                    "hover:scale-[1.02]": !props.selectedIds.includes(t._id),
                  },
                )}
                style={{
                  borderRadius: "var(--radius-4)",
                }}
                onMouseEnter={() => setHoveredId(t._id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <TicketCard
                  {...t}
                  title={title}
                  isPending={false}
                  isInProgress={props.isInProgress.has(t._id)}
                  isRemoving={props.isRemoving.has(t._id)}
                  isDropTarget={props.dropTargetId === t._id}
                  totalRevisions={"totalRevisions" in t ? t.totalRevisions : 0}
                  onDropCards={props.onDropCards}
                  onDeleteTicket={props.onDeleteTicket}
                  revisionMutation={props.revisionMutation}
                  onDeleteFolder={props.onDeleteFolder}
                  onRenameFolder={props.onRenameFolder}
                  onShareTicket={props.onShareTicket}
                  onShareFolder={props.onShareFolder}
                  onDownloadFolder={props.onDownloadFolder}
                  onDownloadTicket={props.onDownloadTicket}
                  onManageTicketVersions={props.onManageTicketVersions}
                  isUploading={true}
                  uploadState={props.uploadState}
                  onCancelUpload={props.onCancelUpload}
                />
              </div>
            );
          }

          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              key={t._id}
              data-card-id={t._id}
              className={cx(
                "relative z-[1] border-2 transition-all duration-200 ease-out",
                {
                  "border-puntt-blue-9": props.selectedIds.includes(t._id),
                  "border-transparent": !props.selectedIds.includes(t._id),
                  "opacity-50":
                    props.isDraggingCards && props.selectedIds.includes(t._id),
                  "scale-[1.02]": props.selectedIds.includes(t._id),
                  "hover:scale-[1.04]": props.selectedIds.includes(t._id),
                  "hover:scale-[1.02]": !props.selectedIds.includes(t._id),
                },
              )}
              style={{
                borderRadius: "var(--radius-4)",
              }}
              draggable={true}
              onDragStart={(e) => props.onDragStart(t._id, e)}
              onDragEnter={(e) => props.onDragEnter(t._id, e)}
              onDragLeave={(e) => props.onDragLeave(t._id, e)}
              onDragOver={props.onDragOver}
              onDrop={(e) => props.onDrop(t._id, e)}
              onClick={(e) => props.onSelect(t._id, e)}
              onDoubleClick={() => {
                posthog.capture("double_clicked_tickets_card", {
                  id: t._id,
                  is_folder: t?.isFolder,
                  name: t && ("name" in t ? t.name : t.title),
                });
                const ticket = mergedFoldersAndTickets.find(
                  (m) => m._id === t._id,
                );
                if (ticket?.isFolder) {
                  props.onFolderCardClick(t._id);
                } else {
                  props.onTicketCardClick(t._id);
                }
              }}
              onMouseEnter={() => setHoveredId(t._id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <TicketCard
                {...t}
                title={title}
                isPending={false}
                isInProgress={props.isInProgress.has(t._id)}
                isRemoving={props.isRemoving.has(t._id)}
                isDropTarget={props.dropTargetId === t._id}
                totalRevisions={"totalRevisions" in t ? t.totalRevisions : 0}
                onDropCards={props.onDropCards}
                onDeleteTicket={props.onDeleteTicket}
                revisionMutation={props.revisionMutation}
                onDeleteFolder={props.onDeleteFolder}
                onRenameFolder={props.onRenameFolder}
                onShareTicket={props.onShareTicket}
                onShareFolder={props.onShareFolder}
                onDownloadFolder={props.onDownloadFolder}
                onDownloadTicket={props.onDownloadTicket}
                onManageTicketVersions={props.onManageTicketVersions}
                isAiReviewed={!!(t as any).isAiReviewed}
                aiPersonas={(t as any).aiPersonas || []}
              />
            </div>
          );
        })}
      </Flex>

      {/* Selection rectangle */}
      {selectionRect && (
        <div
          ref={selectionBoxRef}
          className="pointer-events-none absolute border-2 border-puntt-blue-9 bg-egyptian-blue-500/20"
          style={{
            left: Math.min(selectionRect.startX, selectionRect.currentX),
            top: Math.min(selectionRect.startY, selectionRect.currentY),
            width: Math.abs(selectionRect.currentX - selectionRect.startX),
            height: Math.abs(selectionRect.currentY - selectionRect.startY),
          }}
        />
      )}
    </div>
  );
}

function loadingAction(
  isUploading: boolean,
  isLoading: boolean,
  isInProgress: boolean,
  isRemoving: boolean,
) {
  if (isUploading) {
    return "Uploading…";
  }
  if (isLoading) {
    return "Preparing…";
  }
  if (isInProgress) {
    return "Updating…";
  }
  if (isRemoving) {
    return "Relocating…";
  }
  return null;
}

function SkeletonFolder(props: { directoryName: string }) {
  const { directoryName } = props;

  return (
    <Card
      className="select-none"
      style={{
        width: "308px",
        height: "257.75px",
        padding: "12px",
        boxSizing: "border-box",
      }}
    >
      <Grid width="100%" height="100%" className="fit-content" gap="4">
        {/* Folder tab dip */}
        <div className="absolute right-0 top-0 z-[100] h-6 w-[10.5rem] cursor-default rounded-bl-full rounded-tr-lg border border-b-puntt-neutral-gray-6 border-l-puntt-neutral-gray-6 bg-puntt-neutral-gray-3 before:absolute before:left-[calc(-1px-1.75rem)] before:top-0 before:border-l-[3rem] before:border-t-[calc(1.5rem-1px)] before:border-l-transparent before:border-t-puntt-neutral-gray-6 after:absolute after:-left-7 after:-top-px after:border-l-[3rem] after:border-t-[1.5rem] after:border-l-transparent after:border-t-puntt-neutral-gray-3" />

        <AspectRatio ratio={16 / 9} className="grid place-content-center">
          <Grid className="box-border size-full place-content-center place-items-center gap-3 rounded-lg pt-4">
            <Spinner />
            <Text size="1" className="text-puntt-cool-gray-11">
              {loadingAction(true, false, false, false)}
            </Text>
          </Grid>
        </AspectRatio>

        <Grid gap="4">
          <Heading size="3" className="truncate">
            {directoryName}
          </Heading>
        </Grid>
      </Grid>
    </Card>
  );
}

// Global state to track thumbnail loading
const pendingThumbnails: string[] = [];
const pendingFolderThumbnails: string[] = [];
const thumbnailCache = new Map<string, string[]>();
let isLoadingBatch = false;

async function loadThumbnailBatch() {
  if (isLoadingBatch) return;
  isLoadingBatch = true;

  try {
    // Take up to 5 pending thumbnails
    const batchIds = Array.from(pendingThumbnails).slice(0, 5);
    if (batchIds.length === 0) return;

    // Load thumbnails in parallel
    const results = await Promise.all(
      batchIds.map(async (id) => {
        try {
          const ticket = pendingFolderThumbnails.includes(id)
            ? await getFoldersAndTickets({ folderId: id }, id)
            : await getProject(id);

          if (!ticket) return;
          if (
            "revisionBoards" in ticket &&
            ticket.revisionBoards?.[0]?.errorDetails?.reason
          ) {
            return { id, success: false, error: true };
          }
          if (ticket.thumbnails?.length) {
            thumbnailCache.set(id, ticket.thumbnails);
            pendingThumbnails.splice(pendingThumbnails.indexOf(id), 1);
          }
          return { id, success: true };
        } catch (error) {
          console.error("Error fetching ticket:", error);
          pendingThumbnails.splice(pendingThumbnails.indexOf(id), 1);
          return { id, success: false };
        }
      }),
    );

    // Force re-render of components waiting for these thumbnails
    document.dispatchEvent(
      new CustomEvent("thumbnailsUpdated", {
        detail: {
          ids: results.map((r) => r?.id),
          errorIds: results.filter((r) => r?.error).map((r) => r?.id),
        },
      }),
    );
  } finally {
    isLoadingBatch = false;
  }
}

// Start periodic loading of thumbnail batches
setInterval(loadThumbnailBatch, 5000);

function ThumbnailLoading(props: { ticketId: string; isFolder: boolean }) {
  const [thumbnails, setThumbnails] = useState<string[] | null>(null);
  const [isError, setIsError] = useState(false);
  const [, setTries] = useState(0);

  useEffect(() => {
    // Check cache first
    const cached = thumbnailCache.get(props.ticketId);
    if (cached) {
      setThumbnails(cached);
      return;
    }

    // Add to pending set if not already loading
    if (!pendingThumbnails.includes(props.ticketId)) {
      pendingThumbnails.push(props.ticketId);
      if (props.isFolder) {
        pendingFolderThumbnails.push(props.ticketId);
      }
    }

    // Listen for updates
    const handleUpdate = (e: CustomEvent) => {
      if (e.detail.errorIds.includes(props.ticketId)) {
        setIsError(true);
        return;
      }

      if (e.detail.ids.includes(props.ticketId)) {
        const newThumbnails = thumbnailCache.get(props.ticketId);
        if (newThumbnails) {
          setThumbnails(newThumbnails);
        } else {
          setTries((t) => {
            if (t >= 60) {
              // 5 minutes (5s * 60)
              setIsError(true);
              pendingThumbnails.splice(
                pendingThumbnails.indexOf(props.ticketId),
                1,
              );
              console.error(
                "Timed out fetching thumbnail for ticket",
                props.ticketId,
              );
              return t;
            }
            return t + 1;
          });
        }
      }
    };

    document.addEventListener(
      "thumbnailsUpdated",
      handleUpdate as EventListener,
    );
    return () => {
      document.removeEventListener(
        "thumbnailsUpdated",
        handleUpdate as EventListener,
      );
      pendingThumbnails.splice(pendingThumbnails.indexOf(props.ticketId), 1);
    };
  }, [props.ticketId]);

  if (isError) {
    return <File className="text-puntt-cool-gray-11" size={46} />;
  }
  if (thumbnails?.length) {
    if (!props.isFolder) {
      return (
        <img
          src={thumbnails[0]}
          alt="Project thumbnail"
          className="pointer-events-none size-full select-none object-cover"
          data-testid={`thumbnail-loaded-${props.ticketId}`}
        />
      );
    }
    return (
      <Flex gap="3" className="size-full rounded-xl bg-puntt-cool-gray-5 p-2">
        <Grid className="max-w-[11.25rem] place-content-center">
          <img
            alt="Thumbnail 1 in folder"
            src={thumbnails[0]}
            className="block size-full rounded-xl border border-puntt-neutral-gray-6 object-cover"
            data-testid={`thumbnail-loaded-${props.ticketId}`}
          />
        </Grid>
        <Flex gap="3" direction="column" className="h-full w-[8.5rem]">
          {thumbnails[1] ? (
            <AspectRatio ratio={4 / 3}>
              <img
                alt="Thumbnail 2 in folder"
                src={thumbnails[1]}
                className="size-full rounded-xl border border-puntt-neutral-gray-6 object-cover"
                data-testid={`thumbnail-loaded-${props.ticketId}-2`}
              />
            </AspectRatio>
          ) : null}
          <div className="flex h-full items-center justify-center rounded-xl bg-puntt-cool-gray-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="rounded-xl text-puntt-cool-gray-1"
            >
              <circle cx="5" cy="12" r="2" fill="currentColor" />
              <circle cx="12" cy="12" r="2" fill="currentColor" />
              <circle cx="19" cy="12" r="2" fill="currentColor" />
            </svg>
          </div>
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      {/* For some reason, CI and local Prettier disagree on this class order. */}
      {/* prettier-ignore */}
      <div
        className="origin-center animate-shrink-grow"
        data-testid={`thumbnail-loading-${props.ticketId}`}
      >
        <BreatheLoader />
      </div>
      <Text size="1" className="text-puntt-cool-gray-11">
        Generating thumbnail&hellip;
      </Text>
    </>
  );
}

// Shared avatar rendering for AI personas
export function renderAIPersonaAvatar(persona?: string) {
  let bgClass = "bg-amber-amber10";
  let icon: JSX.Element = (
    <Robot className="size-4 text-base-black" size={16} />
  );
  if (persona) {
    switch (persona.toLowerCase()) {
      case "legal":
        bgClass = "bg-teal-teal7";
        icon = <LegalRobot className="size-4 text-base-black" />;
        break;
      case "copy":
      case "text":
      case "typography":
        bgClass = "";
        icon = <CopyRobot className="size-4 text-base-black" />;
        break;
      case "brand":
        bgClass = "bg-amber-amber10";
        icon = <BrandRobot className="size-4 text-base-black" />;
        break;
      default:
        // Use default robot icon and bgClass
        break;
    }
  }
  // For Copy, use a custom lighter blue background
  const style =
    persona &&
    (persona.toLowerCase() === "copy" ||
      persona.toLowerCase() === "text" ||
      persona.toLowerCase() === "typography")
      ? { backgroundColor: "#A5B4FC" }
      : undefined;
  return (
    <div
      className={`flex size-6 items-center justify-center rounded-full ${bgClass}`}
      style={style}
    >
      {icon}
    </div>
  );
}

export function TicketCard(
  props: GetTicketsResponse[number] &
    Omit<
      TicketProps,
      | "tickets"
      | "onFolderCardClick"
      | "onTicketCardClick"
      | "isInProgress"
      | "isRemoving"
      | "isDropTarget"
    > & {
      isFolder?: boolean;
      isInProgress: boolean;
      isRemoving: boolean;
      isDropTarget: boolean;
      isAiReviewed?: boolean;
      aiPersonas?: string[];
      isUploading?: boolean;
      uploadState?: any;
      onCancelUpload?: () => void;
    },
) {
  const {
    _id,
    documentImportType,
    participants,
    thumbnails = [],
    title,
    totalFiles,
    totalRevisions,
    createdAt,
    updatedAt,
    onDeleteTicket,
    revisionMutation,
    isFolder,
    onDeleteFolder,
    onRenameFolder,
    onShareFolder,
    onShareTicket,
    onDownloadFolder,
    onDownloadTicket,
    isInProgress,
    isRemoving,
    isDropTarget,
    onManageTicketVersions,
    isAiReviewed,
    aiPersonas,
    isUploading = false,
    uploadState,
    onCancelUpload,
  } = props;

  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  // It'd be nice if the backend gave us info from nested folders, but it
  // doesn't because it's slow. Since we already have the folder tree for the
  // AppDrawer, we just use that to check if this folder has any child folders.
  // This is used to decide what icon to show in the folder card.
  const { folderTree } = (useLoaderData({ from: authLayoutRoute.id }) ?? {
    folderTree: [],
  }) as { folderTree: any[] };
  function findFolder(
    folder: (typeof folderTree)[number],
  ): (typeof folderTree)[number] | undefined {
    if (folder._id === _id) {
      return folder;
    }
    for (const childFolder of folder.folders ?? []) {
      const found = findFolder(childFolder);
      if (found) return found;
    }
  }
  const hasChildFolders = Boolean(
    findFolder({ _id: "", name: "", folders: folderTree })?.folders?.length,
  );

  useEffect(() => {
    if (revisionMutation.isSuccess) {
      setIsLoading(false);
    }
  }, [revisionMutation.isSuccess]);

  function renderRevision() {
    if (totalRevisions < 2) {
      return null;
    }

    return (
      <Badge size="1" color="gray" variant="solid">
        V{totalRevisions}
      </Badge>
    );
  }

  function renderDocType() {
    if (totalFiles > 1) {
      return <Text size="2">{totalFiles} Files</Text>;
    }

    if (totalFiles === 1) {
      return <Text size="2">{documentImportType}</Text>;
    }

    if (documentImportType === "" && totalFiles === 0) {
      return (
        <Text size="2">
          {title.includes(".")
            ? title.slice(title.lastIndexOf(".") + 1).toLocaleUpperCase()
            : "EMPTY"}
        </Text>
      );
    }

    return null;
  }

  // Filter out AI Reviewer from participants
  const filteredParticipants: {
    _id?: string;
    avatar?: string;
    name?: string;
  }[] = (participants ?? []).filter(
    (p): p is { _id?: string; avatar?: string; name?: string } =>
      Boolean(p && typeof p === "object" && p.name !== "AI Reviewer"),
  );

  // Fix for folder thumbnail ternary condition
  const isPdfOrNoThumb =
    (Boolean(thumbnails[0]) && thumbnails[0]!.endsWith(".pdf")) ||
    !thumbnails[0];

  const folderCard = () => (
    <Card
      data-testid={`folder-card-${_id}`}
      className={cx(
        "relative cursor-pointer select-none overflow-hidden transition-all duration-200 ease-out hover:bg-puntt-blue-3 hover:shadow-lg hover:shadow-puntt-blue-3/20 active:scale-[0.98] active:bg-puntt-blue-4",
        {
          "opacity-30": isRemoving,
        },
      )}
    >
      {/* Folder tab dip */}
      <div className="absolute right-0 top-0 z-[100] h-6 w-[10.5rem] cursor-default rounded-bl-full rounded-tr-lg border border-b-puntt-neutral-gray-6 border-l-puntt-neutral-gray-6 bg-puntt-neutral-gray-3 before:absolute before:left-[calc(-1px-1.75rem)] before:top-0 before:border-l-[3rem] before:border-t-[calc(1.5rem-1px)] before:border-l-transparent before:border-t-puntt-neutral-gray-6 after:absolute after:-left-7 after:-top-px after:border-l-[3rem] after:border-t-[1.5rem] after:border-l-transparent after:border-t-puntt-neutral-gray-3" />

      <Grid
        width="284px"
        height="164px"
        maxHeight="164px"
        gap="4"
        className="mb-3 overflow-hidden pt-6"
      >
        <AspectRatio
          ratio={81 / 40}
          className={cx("overflow-hidden", {
            "grid place-content-center":
              isLoading || isInProgress || isRemoving,
          })}
          style={
            isRemoving
              ? {
                  ["--loader-spinner-color" as string]:
                    "rgb(var(--puntt-red-9))",
                }
              : undefined
          }
        >
          {isLoading || isInProgress || isRemoving ? (
            <Grid className="size-full place-content-center place-items-center gap-3 rounded-lg">
              <Spinner />
              <Text size="1" className="text-puntt-cool-gray-11">
                {loadingAction(false, isLoading, isInProgress, isRemoving)}
              </Text>
            </Grid>
          ) : isDropTarget ? (
            <Grid className="size-full place-content-center place-items-center gap-3 rounded-lg bg-puntt-blue-9">
              <FolderPlus className="text-base-white" size={46} />
              <Text size="2" className="text-base-white">
                Add to folder
              </Text>
            </Grid>
          ) : isPdfOrNoThumb ? (
            <Grid className="size-full place-content-center place-items-center rounded-lg bg-puntt-cool-gray-5">
              {thumbnails[0] && thumbnails[0].endsWith(".pdf") ? (
                <FilePdf className="text-puntt-cool-gray-11" size={46} />
              ) : totalFiles === 0 ? (
                <Grid className="size-full place-content-center place-items-center gap-3 rounded-lg">
                  {hasChildFolders ? (
                    <Folders className="text-puntt-cool-gray-11" size={46} />
                  ) : (
                    <>
                      <FolderOpen
                        className="text-puntt-cool-gray-11"
                        size={46}
                      />
                      <Text size="1" className="text-puntt-cool-gray-11">
                        No files yet
                      </Text>
                    </>
                  )}
                </Grid>
              ) : new Date(createdAt).getTime() <
                Date.now() - 1000 * 60 * 10 ? (
                <File className="text-puntt-cool-gray-11" size={46} />
              ) : (
                <ThumbnailLoading ticketId={_id} isFolder={true} />
              )}
            </Grid>
          ) : (
            <Flex
              gap="3"
              className="size-full rounded-xl bg-puntt-cool-gray-5 p-2"
            >
              <Grid className="max-w-[11.25rem] place-content-center">
                <img
                  alt="Thumbnail 1 in folder"
                  src={thumbnails[0]}
                  className="block size-full rounded-xl border border-puntt-neutral-gray-6 object-cover"
                />
              </Grid>
              <Flex gap="3" direction="column" className="h-full w-[8.5rem]">
                {thumbnails[1] ? (
                  <AspectRatio ratio={4 / 3}>
                    <img
                      alt="Thumbnail 2 in folder"
                      src={thumbnails[1]}
                      className="size-full rounded-xl border border-puntt-neutral-gray-6 object-cover"
                    />
                  </AspectRatio>
                ) : null}
                <div className="flex h-full items-center justify-center rounded-xl bg-puntt-cool-gray-8">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="rounded-xl text-puntt-cool-gray-1"
                  >
                    <circle cx="5" cy="12" r="2" fill="currentColor" />
                    <circle cx="12" cy="12" r="2" fill="currentColor" />
                    <circle cx="19" cy="12" r="2" fill="currentColor" />
                  </svg>
                </div>
              </Flex>
            </Flex>
          )}
        </AspectRatio>
      </Grid>

      <Grid gap="4" width="284px">
        <Flex gap="4" justify="between" align="center">
          <Heading as="h3" size="3" className="truncate">
            <Link
              to={ticketsRoute.to}
              search={{
                folderId: _id,
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const url = router.buildLocation({
                  to: ticketsRoute.to,
                  search: { folderId: _id },
                });
                window.open(url.href, "_blank");
              }}
            >
              {/* @ts-expect-error TS:2399 its going off of ticket props */}
              {props.name}
            </Link>
          </Heading>
          <TicketActionsDropdown
            className={cx({ hidden: isPunttGuest() })}
            onDeleteTicket={() => onDeleteFolder(_id)}
            onRename={() => onRenameFolder(_id)}
            onShare={() => (isFolder ? onShareFolder(_id) : onShareTicket(_id))}
            onDownload={() => onDownloadFolder(_id)}
          />
        </Flex>

        <Flex justify="between" align="center" minHeight="1.25rem">
          <span />
          <Flex gap="4">
            {/* AI Review Icon - only if isAiReviewed is true */}
            {isAiReviewed && (
              <HoverCard.Root>
                <HoverCard.Trigger>
                  <IconButton
                    variant="ghost"
                    color="gray"
                    data-testid="ai-review-icon"
                  >
                    <Robot />
                  </IconButton>
                </HoverCard.Trigger>
                <HoverCard.Content className="dark" side="top">
                  <Grid gap="2">
                    {Array.isArray(aiPersonas) && aiPersonas.length > 0 ? (
                      aiPersonas.map((persona) => {
                        const normalized = persona?.toLowerCase();
                        const isKnown =
                          normalized === "brand" ||
                          normalized === "legal" ||
                          normalized === "copy";
                        return (
                          <Flex gap="3" align="center" key={persona}>
                            {renderAIPersonaAvatar(
                              isKnown ? normalized : undefined,
                            )}
                            <Text size="1">
                              {isKnown
                                ? `Puntt AI: ${persona.charAt(0).toUpperCase() + persona.slice(1)}`
                                : "Puntt AI"}
                            </Text>
                          </Flex>
                        );
                      })
                    ) : (
                      <Flex gap="3" align="center">
                        {renderAIPersonaAvatar(undefined)}
                        <Text size="1">Puntt AI</Text>
                      </Flex>
                    )}
                  </Grid>
                </HoverCard.Content>
              </HoverCard.Root>
            )}
            <Tooltip
              content={`Last updated: ${
                updatedAt !== ""
                  ? formatDistanceToNow(new Date(updatedAt), {
                      addSuffix: true,
                    })
                  : "--"
              }`}
            >
              <IconButton
                variant="ghost"
                color="gray"
                data-testid="last-updated-icon"
              >
                <Clock />
              </IconButton>
            </Tooltip>
            {filteredParticipants.length > 0 ? (
              <HoverCard.Root>
                <HoverCard.Trigger>
                  <IconButton
                    variant="ghost"
                    color="gray"
                    data-testid="participants-icon"
                  >
                    <Users />
                  </IconButton>
                </HoverCard.Trigger>
                <HoverCard.Content className="dark" side="top">
                  <Grid gap="2">
                    {filteredParticipants.map((participant) => (
                      <Flex gap="3" align="center" key={participant._id}>
                        <AvatarWithInitials
                          size={6}
                          avatar={participant.avatar}
                          name={participant.name}
                        />
                        <Text size="1" className="truncate">
                          {participant.name}
                        </Text>
                      </Flex>
                    ))}
                  </Grid>
                </HoverCard.Content>
              </HoverCard.Root>
            ) : null}
          </Flex>
        </Flex>
      </Grid>
    </Card>
  );

  return (
    <>
      {isFolder ? (
        folderCard()
      ) : (
        <Card
          data-testid={`ticket-card-${_id}`}
          className={cx("select-none", {
            "cursor-pointer transition-all duration-200 ease-out hover:bg-puntt-blue-3 hover:shadow-lg hover:shadow-puntt-blue-3/20 active:scale-[0.98] active:bg-puntt-blue-4":
              !isUploading,
            "opacity-30": isRemoving,
            "pointer-events-none opacity-80": isUploading,
          })}
          tabIndex={isUploading ? -1 : 0}
          aria-disabled={isUploading ? true : undefined}
        >
          <Grid width="284px" height="fit-content" gap="4">
            <AspectRatio
              ratio={16 / 9}
              className={cx("overflow-hidden", {
                "grid place-content-center":
                  isLoading || isInProgress || isRemoving || isUploading,
              })}
              style={
                isRemoving
                  ? {
                      ["--loader-spinner-color" as string]:
                        "rgb(var(--puntt-red-9))",
                    }
                  : undefined
              }
            >
              {isUploading ? (
                <Grid
                  className="size-full place-content-center place-items-center gap-3 rounded-lg"
                  data-testid={`upload-progress-${_id}`}
                >
                  <UploadProgress
                    isUploading={uploadState?.isUploading}
                    progress={uploadState?.progress}
                    fileName={uploadState?.fileName}
                    error={uploadState?.error}
                    isComplete={uploadState?.isComplete}
                    onCancel={onCancelUpload}
                    variant="inline"
                    fixedWidth
                  />
                </Grid>
              ) : isLoading || isInProgress || isRemoving ? (
                <Grid className="size-full place-content-center place-items-center gap-3 rounded-lg">
                  <Spinner />
                  <Text size="1" className="text-puntt-cool-gray-11">
                    {loadingAction(false, isLoading, isInProgress, isRemoving)}
                  </Text>
                </Grid>
              ) : isDropTarget ? (
                <Grid className="size-full place-content-center place-items-center gap-3 rounded-lg bg-puntt-blue-9">
                  <StackPlus className="text-base-white" size={46} />
                  <Text size="2" className="text-base-white">
                    Add as V{totalRevisions + 1}
                  </Text>
                </Grid>
              ) : (Boolean(thumbnails[0]) && thumbnails[0]!.endsWith(".pdf")) ||
                !thumbnails[0]?.length ? (
                <Grid
                  className="size-full place-content-center place-items-center rounded-lg bg-puntt-cool-gray-5"
                  gap="3"
                >
                  {thumbnails[0]?.endsWith(".pdf") ? (
                    <FilePdf className="text-puntt-cool-gray-11" size={46} />
                  ) : (totalFiles === 0 && totalRevisions > 0) ||
                    (totalFiles <= 1 &&
                      (new Date(createdAt).getTime() <
                        Date.now() - 1000 * 60 * 10 ||
                        !SUPPORTED_EXTENSIONS.includes(
                          documentImportType ?? "",
                        ))) ? (
                    <>
                      <File className="text-puntt-cool-gray-11" size={46} />
                      <Text className="font-bold text-puntt-cool-gray-11">
                        {renderDocType()}
                      </Text>
                    </>
                  ) : (
                    <ThumbnailLoading ticketId={_id} isFolder={false} />
                  )}
                </Grid>
              ) : (
                <>
                  <img
                    alt="Project thumbnail"
                    src={thumbnails[0]}
                    className="pointer-events-none size-full select-none object-cover"
                    data-testid={`thumbnail-loaded-${_id}`}
                  />
                  <div
                    className={cx(
                      "absolute left-1/2 top-1/2 size-fit -translate-x-1/2 -translate-y-1/2 rounded-full bg-puntt-neutral-gray-10/25 p-2",
                      {
                        hidden: !VIDEO_EXTENSIONS.includes(
                          documentImportType?.toLowerCase() ?? "",
                        ),
                      },
                    )}
                  >
                    <Play className="text-base-white opacity-75" size={32} />
                  </div>
                </>
              )}
            </AspectRatio>

            <Grid gap="4">
              <Flex gap="4" justify="between" align="center">
                <Heading as="h3" size="3" className="truncate">
                  {isUploading ? (
                    <span className="truncate">{title}</span>
                  ) : (
                    <Link
                      to={ticketRoute.to}
                      params={{ ticketId: _id }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const url = router.buildLocation({
                          to: ticketRoute.to,
                          params: { ticketId: _id },
                        });
                        window.open(url.href, "_blank");
                      }}
                    >
                      {title}
                    </Link>
                  )}
                </Heading>
                {/* Hide 3-dot menu for uploading */}
                {!isUploading && (
                  <TicketActionsDropdown
                    className={cx({ hidden: isPunttGuest() })}
                    onDeleteTicket={() => onDeleteTicket(_id)}
                    onShare={() =>
                      isFolder ? onShareFolder(_id) : onShareTicket(_id)
                    }
                    onDownload={() => onDownloadTicket(_id)}
                    onManageVersions={() => onManageTicketVersions(_id)}
                    onRename={() => onRenameFolder(_id)}
                  />
                )}
              </Flex>
              {/* Show file type for uploading to match real card height */}
              {isUploading && <Text size="2">{documentImportType}</Text>}
              <Flex justify="between" align="center">
                <Flex gap="1">
                  {/* Hide revision badge and doc type for uploading */}
                  {!isUploading && (
                    <>
                      {renderRevision()} {renderDocType()}
                    </>
                  )}
                </Flex>

                <Flex gap="4">
                  {/* Hide last updated and participants for uploading */}
                  {!isUploading && (
                    <>
                      {/* AI Review Icon - only if isAiReviewed is true */}
                      {isAiReviewed && (
                        <HoverCard.Root>
                          <HoverCard.Trigger>
                            <IconButton
                              variant="ghost"
                              color="gray"
                              data-testid="ai-review-icon"
                            >
                              <Robot />
                            </IconButton>
                          </HoverCard.Trigger>
                          <HoverCard.Content className="dark" side="top">
                            <Grid gap="2">
                              {Array.isArray(aiPersonas) &&
                              aiPersonas.length > 0 ? (
                                aiPersonas.map((persona) => {
                                  const normalized = persona?.toLowerCase();
                                  const isKnown =
                                    normalized === "brand" ||
                                    normalized === "legal" ||
                                    normalized === "copy";
                                  return (
                                    <Flex gap="3" align="center" key={persona}>
                                      {renderAIPersonaAvatar(
                                        isKnown ? normalized : undefined,
                                      )}
                                      <Text size="1">
                                        {isKnown
                                          ? `Puntt AI: ${persona.charAt(0).toUpperCase() + persona.slice(1)}`
                                          : "Puntt AI"}
                                      </Text>
                                    </Flex>
                                  );
                                })
                              ) : (
                                <Flex gap="3" align="center">
                                  {renderAIPersonaAvatar(undefined)}
                                  <Text size="1">Puntt AI</Text>
                                </Flex>
                              )}
                            </Grid>
                          </HoverCard.Content>
                        </HoverCard.Root>
                      )}

                      <Tooltip
                        content={`Last updated: ${formatDistanceToNow(new Date(updatedAt), { addSuffix: true })}`}
                      >
                        <IconButton
                          variant="ghost"
                          color="gray"
                          data-testid="last-updated-icon"
                        >
                          <Clock />
                        </IconButton>
                      </Tooltip>

                      {filteredParticipants.length > 0 ? (
                        <HoverCard.Root>
                          <HoverCard.Trigger>
                            <IconButton
                              variant="ghost"
                              color="gray"
                              data-testid="participants-icon"
                            >
                              <Users />
                            </IconButton>
                          </HoverCard.Trigger>
                          <HoverCard.Content className="dark" side="top">
                            <Grid gap="2">
                              {filteredParticipants.map((participant) => (
                                <Flex
                                  gap="3"
                                  align="center"
                                  key={participant._id}
                                >
                                  <AvatarWithInitials
                                    size={6}
                                    avatar={participant.avatar}
                                    name={participant.name}
                                  />
                                  <Text size="1" className="truncate">
                                    {participant.name}
                                  </Text>
                                </Flex>
                              ))}
                            </Grid>
                          </HoverCard.Content>
                        </HoverCard.Root>
                      ) : null}
                    </>
                  )}
                </Flex>
              </Flex>
            </Grid>
          </Grid>
        </Card>
      )}
    </>
  );
}
